import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Button, Input, Select, DatePicker, Checkbox, message } from "antd";
import voucherApi from "../../../api/voucherApi";
import { getUnixTime } from "date-fns";
import dayjs from "dayjs";

const { TextArea } = Input;

function VoucherForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isEdit = !!id;
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        code: "",
        secret_code: "",
        title: "",
        description: "",
        value: "",
        redeem_cost: "",
        partner: "SHOPEE",
        image_url: "",
        target_url: "",
        start_at: null,
        end_at: null,
        app_only: false,
    });

    useEffect(() => {
        if (isEdit) {
            fetchVoucher();
        }
    }, [id]);

    useEffect(() => {
        // Load copied data when creating from copy (only on mount)
        if (!isEdit && location.state?.copiedData) {
            const copiedData = location.state.copiedData;
            setFormData({
                code: copiedData.code || "",
                secret_code: copiedData.secret_code || "",
                title: copiedData.title || "",
                description: copiedData.description || "",
                value: copiedData.value || "",
                redeem_cost: copiedData.redeem_cost ?? "", // giữ 0 khi copy voucher 0đ
                partner: copiedData.partner || "SHOPEE",
                image_url: copiedData.image_url || "",
                target_url: copiedData.target_url || "",
                start_at: copiedData.start_at || null,
                end_at: copiedData.end_at || null,
                app_only: !!copiedData.app_only,
            });
            // Clear state to avoid reloading on re-render
            navigate(location.pathname, { replace: true, state: {} });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchVoucher = async () => {
        try {
            const { data } = await voucherApi.getVoucherById(id);
            const voucher = data.data.voucher;
            setFormData({
                code: voucher.code || "",
                secret_code: voucher.secret_code || "",
                title: voucher.title || "",
                description: voucher.description || "",
                value: voucher.value || "",
                redeem_cost: voucher.redeem_cost ?? "", // giữ 0 (voucher 0đ), không để 0 thành rỗng
                partner: voucher.partner || "SHOPEE",
                image_url: voucher.image_url || "",
                target_url: voucher.target_url || "",
                start_at: voucher.start_at ? dayjs.unix(voucher.start_at) : null,
                end_at: voucher.end_at ? dayjs.unix(voucher.end_at) : null,
                app_only: !!voucher.app_only,
            });
        } catch (error) {
            message.error("Không thể tải thông tin voucher");
            navigate("/voucher");
        }
    };

    const handleSubmit = async () => {
        // Validation
        if (!formData.code || !formData.title || !formData.value || !formData.partner || !formData.start_at || !formData.end_at) {
            message.error("Vui lòng điền đầy đủ thông tin bắt buộc");
            return;
        }

        const valueNum = parseInt(formData.value, 10);
        // Giá đổi: bỏ trống → mặc định = mệnh giá; nhập 0 → voucher 0đ (không bị thay bằng value).
        const redeemRaw = formData.redeem_cost;
        const redeemCostNum =
            redeemRaw === "" || redeemRaw === null || redeemRaw === undefined
                ? valueNum
                : parseInt(redeemRaw, 10);

        if (isNaN(valueNum) || valueNum <= 0) {
            message.error("Mệnh giá phải là số nguyên dương");
            return;
        }

        if (isNaN(redeemCostNum) || redeemCostNum < 0) {
            message.error("Giá đổi phải là số nguyên ≥ 0 (0 = voucher miễn phí)");
            return;
        }

        try {
            setLoading(true);
            const submitData = {
                ...formData,
                value: valueNum,
                redeem_cost: redeemCostNum,
                start_at: getUnixTime(formData.start_at.toDate()),
                end_at: getUnixTime(formData.end_at.toDate()),
            };

            if (isEdit) {
                await voucherApi.updateVoucher(id, submitData);
                message.success("Cập nhật voucher thành công");
            } else {
                await voucherApi.createVoucher(submitData);
                message.success("Tạo voucher thành công");
            }
            navigate("/voucher");
        } catch (error) {
            const errorMsg = error.response?.data?.message || "Có lỗi xảy ra";
            message.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6 max-w-4xl mx-auto">
            <h1 className="text-2xl font-bold mb-4">{isEdit ? "Sửa Voucher" : "Tạo Voucher Mới"}</h1>
            <div className="space-y-4">
                <div>
                    <label className="block mb-2">Code *</label>
                    <Input
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                        placeholder="Mã voucher"
                    />
                </div>
                <div>
                    <label className="block mb-2">Secret Code</label>
                    <Input
                        value={formData.secret_code}
                        onChange={(e) => setFormData({ ...formData, secret_code: e.target.value })}
                        placeholder="Mã bí mật (nếu có)"
                    />
                </div>
                <div>
                    <label className="block mb-2">Tiêu đề *</label>
                    <Input
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="Tiêu đề voucher"
                    />
                </div>
                <div>
                    <label className="block mb-2">Mô tả</label>
                    <TextArea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Mô tả voucher"
                        rows={4}
                    />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block mb-2">Mệnh giá *</label>
                        <Input
                            type="number"
                            value={formData.value}
                            onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                            placeholder="10000"
                        />
                    </div>
                    <div>
                        <label className="block mb-2">Giá đổi</label>
                        <Input
                            type="number"
                            value={formData.redeem_cost}
                            onChange={(e) => setFormData({ ...formData, redeem_cost: e.target.value })}
                            placeholder="Mặc định = mệnh giá · nhập 0 = voucher 0đ"
                            min={0}
                        />
                    </div>
                </div>
                <div>
                    <label className="block mb-2">Đối tác *</label>
                    <Select
                        value={formData.partner}
                        onChange={(value) => setFormData({ ...formData, partner: value })}
                        className="w-full"
                    >
                        <Select.Option value="SHOPEE">Shopee</Select.Option>
                        <Select.Option value="LAZADA">Lazada</Select.Option>
                        <Select.Option value="TIKTOK">TikTok</Select.Option>
                        <Select.Option value="OTHER">Khác</Select.Option>
                    </Select>
                </div>
                <div>
                    <label className="block mb-2">Image URL</label>
                    <Input
                        value={formData.image_url}
                        onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                        placeholder="URL ảnh voucher"
                    />
                </div>
                <div>
                    <label className="block mb-2">Target URL</label>
                    <Input
                        value={formData.target_url}
                        onChange={(e) => setFormData({ ...formData, target_url: e.target.value })}
                        placeholder="Link đích khi click"
                    />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block mb-2">Ngày bắt đầu *</label>
                        <DatePicker
                            showTime
                            value={formData.start_at}
                            onChange={(date) => setFormData({ ...formData, start_at: date })}
                            className="w-full"
                        />
                    </div>
                    <div>
                        <label className="block mb-2">Ngày kết thúc *</label>
                        <DatePicker
                            showTime
                            value={formData.end_at}
                            onChange={(date) => setFormData({ ...formData, end_at: date })}
                            className="w-full"
                        />
                    </div>
                </div>
                <div>
                    <Checkbox
                        checked={formData.app_only}
                        onChange={(e) => setFormData({ ...formData, app_only: e.target.checked })}
                    >
                        Chỉ đổi trên App (web vẫn hiện, bấm → mời tải app)
                    </Checkbox>
                </div>
                <div className="flex gap-2">
                    <Button type="primary" onClick={handleSubmit} loading={loading}>
                        {isEdit ? "Cập nhật" : "Tạo mới"}
                    </Button>
                    <Button onClick={() => navigate("/voucher")}>Hủy</Button>
                </div>
            </div>
        </div>
    );
}

export default VoucherForm;

